import { Col, Empty, Row, Spin, message } from "antd";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCookie } from "../../services/cookies";
import { getExamInputs, getPatientExam, validateExamsCriterions, initializeExam, getExamsByType } from "../../services/exam";
import { laboratoryService } from "../../services/laboratory";
import { getEnabledBranch } from "../../services/rx";
import ExamInputs from "./components/ExamInputs";
import Header from "./components/Header";
import MedicalConsultationHeader from "./components/MedicalConsultationHeader";
import countrySettings from './countrySettings.json';
import groupedExams from '../../utils/groupedExams.json';
import { getActiveProviders } from "../../services/providers";
import { triggerOperativeConditionals } from "../../services/admission";
import { getGroupedAndSortedLaboratoryExams, getLaboratoryCategoryInfo } from "./components/utils/laboratoryLayoutHelper";

const ExamDetailPage = ({
  fromExternal,
  inSidebarMode = false,
  consultationExamsData,
  setConsultationExamsData,
  services,
  externalAdmissionPatientExamId,
  externalExam,
  from,
  serviceParams,
  externalGroupType,
  refreshMedicalConsultationDetails,
  onExamSaveReady,
  onLoadingChange,
  readOnly = false,
}) => {
  const [loading, setLoading] = useState({
    inputs: false,
    patient: false,
    examsTabs: false,
    free: false,
    init: false,
    documents: false,
    save: false,
    finish: false,
    downloading: false,
    results: false
  });
  const [examInfo, setExamInfo] = useState({
    exams: [],
    inputs: {},
    patient: undefined,
    isEmpty: true,
    isPsychological: false,
  });
  const [disableForm, setDisableForm] = useState(false);
  const [drugModal, setDrugModal] = useState(false);
  const [examsTabs, setExamsTabs] = useState();
  const { paramsAdmissionPatientExamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const groupType = queryParams.get('groupType') || externalGroupType;
  const isGroupedExams = !!groupType
  const admissionPatientExamIdParam = fromExternal ? externalAdmissionPatientExamId : (externalAdmissionPatientExamId || paramsAdmissionPatientExamId);
  const [admissionPatientExamId, setAdmissionPatientExamId] = useState(!groupType ? admissionPatientExamIdParam : null);
  const [admissionPatientId, setAdmissionPatientId] = useState(groupType ? (admissionPatientExamIdParam || externalExam.admission_patient_id) : null);
  const [refresh, setRefresh] = useState(false);
  const [laboratoryModule, setLaboratoryModule] = useState(false);
  const [laboratoryData, setLaboratoryData] = useState();
  const [enableRx, setEnableRx] = useState(undefined);
  const [enableEcg, setEnableEcg] = useState(false);
  const roles = getCookie('roles');
  const countryCode = getCookie('country_code')
  const isPermittedRole = !roles.includes('branch_office_manager');
  const examRefs = useRef([]);
  const types = ['procedures', 'measurements', 'questions'];

  const fetchPatient = useCallback(async () => {
    setLoading(loading => ({ ...loading, patient: true }));

    try {
      const response = await getPatientExam(admissionPatientExamId || admissionPatientId, { group_type: groupType || 'null' });
      setExamInfo(examInfo => ({ ...examInfo, patient: response }));

      if (Object.keys(groupedExams).includes(response?.exam_type) || isGroupedExams) {
        fetchExamsTabs(response);
      }
      setAdmissionPatientId(response.admission_patient_id);
    } catch (error) {
      console.error(`Error al obtener Datos de paciente: ${error}`);
      fetchMessage('error', `Error al obtener Datos de paciente: ${error}`);
    } finally {
      setLoading(loading => ({ ...loading, patient: false }));
    }
  }, [admissionPatientExamId]);

  const fetchResultTypes = async (exam, id) => {
    try {
      const response = await validateExamsCriterions({ id });
      exam.measurements?.forEach((measurement) => {
        const resultType = response?.measurements?.find((_measurement) => _measurement.measurement_id === measurement.id)?.result_type;
        measurement.result_type = resultType;
      })
      exam.procedures?.forEach((procedure) => {
        const resultType = response?.procedures?.find((_procedure) => _procedure.procedure_id === procedure.id)?.result_type;
        procedure.result_type = resultType;
      })
    } catch (error) {
      console.error(error);
      fetchMessage('error', `Error al obtener los datos del examen`);
    }
    return exam;
  }

  const fetchInputs = useCallback(async () => {
    setLoading(loading => ({ ...loading, inputs: true }));
    try {
      if (examsTabs && isGroupedExams) {

        const response = await Promise.all(examsTabs.filter(tab => tab.id !== `${admissionPatientId}?groupType=${groupType}`).map(async (exam) => {
          let response = await getExamInputs({ admissionPatientExamId: exam.id });
          if (externalExam && from === 'consultation') {
            response = await fetchResultTypes(response, exam.id);
          }

          const isPsychological = response.department_id === 2;
          return {
            ...exam,
            inputs: {
              ...response,
              exam_group: exam.exam_group || null
            },
            isEmpty: !types.some(type => response?.[type]?.some(item => !!item.result_id)) && !response.to_doctor,
            isPsychological: isPsychological
          };
        }));
        setExamInfo(examInfo => ({ ...examInfo, exams: response }));
        if (fromExternal && from === 'consultation') {
          setConsultationExamsData(prevExams => [...prevExams, ...response]);
        }
        if (readOnly) {
          setDisableForm(true);
        }
      }

      if (admissionPatientExamId) {
        let response = await getExamInputs({ admissionPatientExamId, fromExamModule: true });

        const isPsychological = response.department_id === 2;
        if (!readOnly
          && ((fromExternal && from === 'expert') || (fromExternal && from === 'consultation' && isPsychological))
          && ['created', 'reevaluate'].includes(response.state)) {
          try {
            await initializeExam({ admissionPatientExamId });
            response = await getExamInputs({ admissionPatientExamId, fromExamModule: true });
          } catch (error) {
            console.error('Error initializing psychological exam:', error);
            fetchMessage('error', 'Error al inicializar el examen psicológico');
          }
        }

        if (externalExam && from === 'consultation') {
          response = await fetchResultTypes(response, externalExam.admission_patient_exam_id);
        }

        const matchingTab = examsTabs?.find(tab => String(tab.id) === String(admissionPatientExamId));
        const resolvedExamGroup = matchingTab?.exam_group || null;

        setExamInfo(examInfo => ({
          ...examInfo,
          exams: [{
            id: admissionPatientExamId,
            inputs: {
              ...response,
              exam_group: resolvedExamGroup
            },
            isEmpty: !types.some(type => response?.[type]?.some(item => !!item.result_id)) && !response.to_doctor,
            isPsychological: isPsychological
          }],
          inputs: {
            ...response,
            exam_group: resolvedExamGroup
          },
        }));
        if (fromExternal && from === 'consultation') {
          setConsultationExamsData(prevExams => [...prevExams, { id: admissionPatientExamId, inputs: { ...response, exam_group: resolvedExamGroup } }]);
        }
        setDisableForm(readOnly || !['inProgress', 'occupied', 'done'].includes(response.state));
      }
    } catch (error) {
      console.error(error);
      fetchMessage('error', `Error al obtener los datos del examen`);

    } finally {
      setLoading(loading => ({ ...loading, inputs: false }));
    }
  }, [examsTabs, isGroupedExams, admissionPatientExamId, admissionPatientId, groupType, externalExam, types, fromExternal, setConsultationExamsData]);

  const fetchLaboratoryIntegration = useCallback(async (branchOfficeId) => {
    setLoading(loading => ({ ...loading, exams: true }));
    try {
      const providers = getActiveProviders();
      setLaboratoryModule(providers?.[branchOfficeId]?.laboratory ? true : false);
      setEnableEcg(providers?.[branchOfficeId]?.ecg?.includes('edx-ecg') ?? false);
    } catch (error) {
      console.error(`Error al obtener Integración de laboratorio: ${error}`);
      fetchMessage('error', `Error al obtener Integración de laboratorio: ${error}`);
    } finally {
      setLoading(loading => ({ ...loading, exams: false }));
    }
  }, []);

  const fetchRxIntegration = useCallback(async () => {
    setLoading(loading => { return { ...loading, exams: true } });
    try {
      const response = await getEnabledBranch({ branchOfficeId: getCookie('branch_office_id') });
      setEnableRx(response ?? null);
    } catch (error) {
      console.error(`Error al obtener datos de Rx: ${error}`);
      fetchMessage('error', `Error al obtener datos de Rx: ${error}`);
      setEnableRx(null);
    } finally {
      setLoading(loading => { return { ...loading, exams: false } });
    }

  }, []);

  const fetchExamsTabs = useCallback(async (params) => {
    setLoading(loading => ({ ...loading, examsTabs: true }));
    try {
      const activeGroupType = params.exam_type || groupType;
      const response = await getExamsByType({
        admission_patient_id: params.admission_patient_id,
        exam_type: activeGroupType,
        ...(
          (serviceParams)
          && serviceParams
        ),
      })
      setExamsTabs([
        {
          id: `${params.admission_patient_id}?groupType=${activeGroupType}`,
          name: 'Todos',
        },
        ...response
      ]);
    } catch (error) {
      console.error(`Error al obtener los Exámenes de los tabs: ${error}`);
      fetchMessage('error', `Error al obtener los Exámenes de los tabs: ${error}`);
    } finally {
      setLoading(loading => ({ ...loading, examsTabs: false }));
    }
  }, [countryCode, groupType, serviceParams]);

  const handleSaveAllExams = useCallback(async () => {
    try {
      setLoading(state => ({ ...state, save: true }));
      const promises = examRefs.current.map(examRef => {
        if (examRef) { 
          return examRef.handleSave();
        }
        return null;
      });
      await Promise.all(promises);

      const triggerConditionals = countrySettings?.saveExams?.triggerOperativeConditionals?.[countryCode];
      if (triggerConditionals && admissionPatientId) {
        try {
          await triggerOperativeConditionals({ admissionPatientId });
        } catch (err) {
          console.error('Error triggering operative conditionals', err);
        }
      }

      if (isGroupedExams || (examInfo.patient && Object.keys(groupedExams).includes(examInfo.patient.exam_type))) {
        await fetchExamsTabs({ 
          admission_patient_id: admissionPatientId,
          exam_type: examInfo.patient?.exam_type
        });
      }
    } catch (error) {
      fetchMessage('error', `Error al guardar los exámenes: ${error}`);
      console.error('Error, ', error);
    } finally {
      setLoading(state => ({ ...state, save: false }));
    }
  }, [admissionPatientId, fetchExamsTabs, countryCode, isGroupedExams, examInfo.patient]);

  const handleFinishAllExams = useCallback(async () => {
    try {
      setLoading(state => ({ ...state, finish: true }));
      const promises = examRefs.current.map(examRef => {
        if (examRef) { 
          return examRef.handleFinish();
        }
        return null;
      });
      await Promise.all(promises);

      const triggerConditionals = countrySettings?.saveExams?.triggerOperativeConditionals?.[countryCode];
      if (triggerConditionals && admissionPatientId) {
        try {
          await triggerOperativeConditionals({ admissionPatientId });
        } catch (err) {
          console.error('Error triggering operative conditionals', err);
        }
      }

      if (isGroupedExams || (examInfo.patient && Object.keys(groupedExams).includes(examInfo.patient.exam_type))) {
        await fetchExamsTabs({ 
          admission_patient_id: admissionPatientId,
          exam_type: examInfo.patient?.exam_type
        });
      }
    } catch (error) {
      fetchMessage('error', `Error al finalizar los exámenes: ${error}`);
      console.error('Error, ', error);
    } finally {
      setLoading(state => ({ ...state, finish: false }));
    }
  }, [admissionPatientId, fetchExamsTabs, countryCode, isGroupedExams, examInfo.patient]);

  useEffect(() => {
    if (onExamSaveReady) {
      onExamSaveReady(handleSaveAllExams);
    }
  }, [onExamSaveReady, handleSaveAllExams]);

  const getStructuredResults = async ({ admissionPatientId }) => {
    if (admissionPatientId) {
      setLoading(loading => ({ ...loading, results: true }));
      try {
        const response = await laboratoryService.getStructuredResults({
          admissionPatientId: admissionPatientId,
        });

        if (response) {
          setLaboratoryData(response);
        }
        setLoading(loading => { return { ...loading, results: false } })
      } catch (error) {
        fetchMessage('error', `Error al obtener resultados: ${error}`);
        console.error('Error, ', error);
      }

      setLoading(loading => ({ ...loading, inputs: false }));
    }
  };

  useEffect(() => {
    fetchPatient();
  }, [admissionPatientExamId, fetchPatient]);

  useEffect(() => {
    if (examInfo.patient?.branch_office_id) {
      fetchLaboratoryIntegration(examInfo.patient.branch_office_id);
    }
    fetchRxIntegration();
  }, [examInfo.patient, fetchLaboratoryIntegration, fetchRxIntegration]);

  useEffect(() => {
    if (
      laboratoryModule &&
      examInfo.patient?.exam_type === 'laboratory' &&
      examInfo.patient?.admission_patient_id
    ) {
      getStructuredResults({ admissionPatientId: examInfo.patient.admission_patient_id });
    }
  }, [laboratoryModule, examInfo.patient]);

  useEffect(() => {
    if (admissionPatientExamId || (examsTabs && isGroupedExams)) {
      fetchInputs();
    }
  }, [admissionPatientExamId, examsTabs, isGroupedExams])

  const fetchMessage = (type, text) => {
    const parsedType = type.toLowerCase();
    message[parsedType]({
      content: text,
      style: {
        marginTop: "35vh",
        fontSize: 16,
      },
    });
  };

  const updateAdmissionPatientExams = useCallback(() => {
    if (paramsAdmissionPatientExamId && !isGroupedExams && !fromExternal) {
      setAdmissionPatientExamId(paramsAdmissionPatientExamId);
    }
  }, [paramsAdmissionPatientExamId]);

  useEffect(() => {
    updateAdmissionPatientExams();
  }, [updateAdmissionPatientExams]);

  const getTranslationKey = (type) => {
    if (isGroupedExams && examInfo?.exams?.length > 1) {
      return `${type}Exams`;
    }

    return `${type}Exam`;
  };

  const isCurrentlyLoading = Object.values(loading).some((value) => value);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isCurrentlyLoading);
    }
  }, [isCurrentlyLoading, onLoadingChange]);

  const isLaboratoryLayout = groupType === 'laboratory' && countrySettings?.laboratoryFormLayout?.[countryCode];
  const groupedLaboratoryExams = useMemo(() => {
    if (!isLaboratoryLayout) return null;
    return getGroupedAndSortedLaboratoryExams(examInfo.exams || []);
  }, [examInfo.exams, isLaboratoryLayout]);

  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const toggleCategoryCollapse = useCallback((category) => {
    setCollapsedCategories(previousCollapsed => {
      const nextCollapsed = new Set(previousCollapsed);
      if (nextCollapsed.has(category)) {
        nextCollapsed.delete(category);
      } else {
        nextCollapsed.add(category);
      }
      return nextCollapsed;
    });
  }, []);

  return (
    <Row justify="center" className="exam-detail" gutter={[0, `${fromExternal ? 0 : 40}`]}>
      <Col lg={24} xs={24}>
        <Spin
          spinning={isCurrentlyLoading}
          className={`cmt-spin${inSidebarMode ? '' : ' mt-50'}`}
        >
          {
            !fromExternal && examInfo.patient &&
            <Header
              examInfo={examInfo}
              disableForm={disableForm}
              admissionPatientExamId={admissionPatientExamId || examInfo.exams?.[0]?.id}
              setDrugModal={setDrugModal}
              drugModal={drugModal}
              fetchInputs={fetchInputs}
              fetchMessage={fetchMessage}
              navigate={navigate}
              setLoading={setLoading}
              loading={loading}
              admissionPatientId={admissionPatientId}
              examsTabs={examsTabs}
              laboratoryData={laboratoryData}
              fromExternal={fromExternal}
              isPermittedRole={isPermittedRole}
              isGroupedExams={isGroupedExams}
              fetchExamsTabs={fetchExamsTabs}
              getTranslationKey={getTranslationKey}
              enableRx={enableRx}
              laboratoryModule={laboratoryModule}
              groupType={groupType}
            />
          }
          {
            fromExternal &&
            <MedicalConsultationHeader
              admissionPatientExamId={admissionPatientExamId || examInfo.exams?.[0]?.id}
              fetchMessage={fetchMessage}
              setLoading={setLoading}
              examName={externalExam?.name}
              hasPreviousExam={!!externalExam?.has_previous_exam}
              parentAdmissionPatientExamId={externalExam?.parent_admission_patient_exam_id}
              parentAdmissionPatientId={externalExam?.parent_admission_patient_id}
              groupType={groupType || undefined}
              serviceParams={serviceParams}
            />
          }
          {
            (fromExternal && from === 'consultation') && (examInfo.exams?.length && examInfo.exams?.some(exam => exam.isEmpty && !exam.isPsychological)) &&
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='No hay resultados para este examen'
            />
          }
          <Row justify="center" className={`form-wrapper${inSidebarMode ? '' : ' mt-40'}`}>
            {
              (
                (!(fromExternal) && examInfo.patient)
                ||
                ((fromExternal && from === 'consultation') && !(examInfo.exams?.length && examInfo.exams?.some(exam => exam.isEmpty && !exam.isPsychological)))
                ||
                (fromExternal && from === 'expert')

              ) &&
              <>
                {
                  (() => {
                    const sortedExams = isLaboratoryLayout && groupedLaboratoryExams
                      ? groupedLaboratoryExams.flatMap(group => group.exams)
                      : examInfo.exams || [];
                    const examIndexById = new Map((examInfo.exams || []).map((examItem, i) => [examItem.id, i]));
                    const seenCategories = new Set();
                    const seenExamGroupIds = new Set();

                    return sortedExams.map((exam, index) => {
                      const examIndex = examIndexById.get(exam.id) ?? index;
                      const laboratoryCategoryInfo = isLaboratoryLayout
                        ? getLaboratoryCategoryInfo({
                            exam,
                            index,
                            sortedExams,
                            seenCategories,
                            seenExamGroupIds,
                            collapsedCategories,
                            toggleCategoryCollapse,
                          })
                        : null;

                      return (
                        <Col lg={24} xs={24} key={exam.id || index}>
                          <ExamInputs
                            ref={el => examRefs.current[examIndex] = el}
                            from={from}
                            disableForm={disableForm}
                            readOnly={readOnly}
                            inputs={exam.inputs}
                            patient={examInfo.patient}
                            toDoctor={exam.inputs.to_doctor}
                            admissionPatientExamId={exam.id}
                            drugModal={!(fromExternal && from === 'consultation') && drugModal}
                            setDrugModal={setDrugModal}
                            fetchInputs={fetchInputs}
                            admissionPatientId={admissionPatientId}
                            fetchMessage={fetchMessage}
                            fromExternal={fromExternal}
                            fetchExamsTabs={fetchExamsTabs}
                            navigate={navigate}
                            location={location}
                            loading={loading}
                            setLoading={setLoading}
                            setRefresh={setRefresh}
                            examsTabs={examsTabs}
                            isPermittedRole={isPermittedRole}
                            isGroupedExams={isGroupedExams}
                            isLast={index === sortedExams.length - 1}
                            allExams={examInfo.exams}
                            countryCode={countryCode}
                            handleSaveAllExams={handleSaveAllExams}
                            handleFinishAllExams={handleFinishAllExams}
                            getTranslationKey={getTranslationKey}
                            enableRx={enableRx}
                            enableEcg={enableEcg}
                            refreshMedicalConsultationDetails={refreshMedicalConsultationDetails}
                            countrySettings={countrySettings}
                            allExamsInfo={consultationExamsData}
                            setAllExamsInfo={setConsultationExamsData}
                            consultationServices={services}
                            groupType={groupType}
                            laboratoryCategoryInfo={laboratoryCategoryInfo}
                          />
                        </Col>
                      );
                    });
                  })()
                }
            </>
            }
          </Row>
        </Spin>
      </Col>
    </Row>
  );
};

export default ExamDetailPage;